import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  Agent,
  OutboxEvent,
  ProjectAgentAssignment,
  ProjectGraph,
  ProjectInvitation,
  ProjectMemberView,
  ProjectSummary,
  RepositoryView,
  Session,
  TelegramStatus,
} from "@baton/contracts";
import { agentShortName } from "@baton/contracts";
import { ApiError, DashboardApi, type CurrentUser } from "./api.js";
import { CryptoWorkerClient } from "./crypto-client.js";
import type { DecryptedProjectGraph, DecryptedProjectInvitation, DecryptedProjectSummary } from "./projects-view.js";
import { FlowHome } from "./flow-home.js";
import { FlowSidebar } from "./flow-sidebar.js";
import { FlowActionsProvider, PresenceProvider, type AgentCreateSeed, type AgentRef, type FlowActions, type WireRef } from "./flow/nodes.js";
import { buildOptionsFor, type FlowViewer } from "./flow/flow-canvas.js";
import { agentNodeId, buildProjectFlow, type BatonNode } from "./flow/model.js";
import {
  activityFromRealtime,
  createDemoActivityEvent,
  describeActivity,
  pruneActivity,
  reduceActivity,
  type ActivityEvent,
  type RealtimeMessageEvent,
} from "./flow/activity.js";
import { presenceFor, type Presence } from "./chat/thread.js";
import { useRealtime } from "./hooks/use-realtime.js";
import { useDashboardSession } from "./hooks/use-dashboard-session.js";
import { useProjectMessages, type SendDraft } from "./hooks/use-project-messages.js";
import { useCanvasPositions, type PositionChange } from "./hooks/use-canvas-positions.js";
import { AgentChatDrawer, type ChatTarget } from "./agent-chat.js";
import { AgentCreateDialog } from "./agent-create-dialog.js";
import { AgentConnectSheet, type ConnectAgent } from "./agent-connect-sheet.js";
import { InviteDialog } from "./invite-dialog.js";
import { InviteAcceptDialog, type InvitePreviewView } from "./invite-accept.js";
import { RepositoryPicker } from "./repository-picker.js";
import { ProjectCreateDialog } from "./project-create-dialog.js";
import { TelegramDialog } from "./telegram-dialog.js";
import { useProjectPeople } from "./people-list.js";
import { PairPage, PAIR_PATH, readPairCode } from "./pair-page.js";
import { deriveSetupSteps, SetupChecklist, setupProgress, type SetupActionKind } from "./setup-checklist.js";
import { WireComposer } from "./wire-composer.js";
import { readInviteToken, readJoinedProject } from "./invite.js";
import { HandoverDialog, HandoverReader, ProjectMessagesDialog, readHandoverId } from "./handover-dialog.js";

interface Connection {
  api: DashboardApi;
  worker: CryptoWorkerClient;
  user: CurrentUser;
  /** Invite token from the URL this page was opened with; memory only, consumed once. */
  pendingInvite: string | null;
  /** Project the API already joined during GitHub sign-in through an invite link. */
  joinedProjectId: string | null;
  handoverId: string | null;
}

interface BootState {
  invite: string | null;
  joinedProjectId: string | null;
  handoverId: string | null;
  /** This page was opened at `/pair`, so a terminal is waiting on a decision. */
  pairing: boolean;
  /** The pairing code the terminal put in the fragment; memory only from here on. */
  pairCode: string | null;
}

/** Reads invite and pairing context from the URL exactly once and scrubs both out of the address bar. */
function readBootState(): BootState {
  const url = new URL(window.location.href);
  const invite = readInviteToken(url);
  const joinedProjectId = readJoinedProject(url);
  const pairing = url.pathname === PAIR_PATH;
  const pairCode = readPairCode(url);
  // The fragment is the one hop that carried the code; it must not survive the first paint.
  if (pairing && url.hash) window.history.replaceState(null, "", PAIR_PATH);
  if (invite && url.pathname !== "/auth/complete") window.history.replaceState(null, "", "/");
  return { invite, joinedProjectId, pairing, pairCode, handoverId: readHandoverId(url) };
}

export function App() {
  const boot = useRef(readBootState());
  const [connection, setConnection] = useState<Connection | null>(null);
  const [pairing, setPairing] = useState(boot.current.pairing);
  const [openChecklist, setOpenChecklist] = useState(false);
  if (!connection) {
    return <KeyGate
      pendingInvite={boot.current.invite}
      pairing={boot.current.pairing}
      handoverId={boot.current.handoverId}
      onConnected={(api, worker, user) => {
        const joinedProjectId = boot.current.joinedProjectId;
        const pendingInvite = joinedProjectId ? null : boot.current.invite;
        boot.current = { ...boot.current, invite: null, joinedProjectId: null };
        setConnection({ api, worker, user, pendingInvite, joinedProjectId, handoverId: boot.current.handoverId });
      }}
    />;
  }
  if (pairing) {
    return <PairPage
      api={connection.api}
      userHandle={connection.user.handle}
      initialCode={boot.current.pairCode}
      onDone={(outcome) => {
        boot.current = { ...boot.current, pairing: false, pairCode: null };
        window.history.replaceState(null, "", "/");
        // An approved terminal is halfway through setup; hand it the rest of the checklist.
        setOpenChecklist(outcome === "approved");
        setPairing(false);
      }}
    />;
  }
  return <Cockpit connection={connection} openChecklist={openChecklist} onLock={() => {
    connection.worker.destroy();
    setConnection(null);
  }} />;
}

type GitHubAvailability =
  | { state: "unknown" }
  | { state: "configured" }
  | { state: "unconfigured"; setupUrl: string | null };

type TeamKeyMode = "managed" | "client" | "unknown";

function KeyGate({ onConnected, pendingInvite, pairing, handoverId }: {
  onConnected(api: DashboardApi, worker: CryptoWorkerClient, user: CurrentUser): void;
  pendingInvite: string | null;
  /** Opened at `/pair`: a terminal is waiting, and the GitHub callback must come back here. */
  pairing: boolean;
  handoverId: string | null;
}) {
  const [apiUrl, setApiUrl] = useState(import.meta.env.VITE_BATON_API_URL ?? window.location.origin);
  const [token, setToken] = useState("");
  const [teamKey, setTeamKey] = useState("");
  const [keyId, setKeyId] = useState("team-v1");
  const [keyEpoch, setKeyEpoch] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapNotice, setBootstrapNotice] = useState<string | null>(null);
  const [signedInViaGitHub, setSignedInViaGitHub] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(false);
  const [github, setGithub] = useState<GitHubAvailability>({ state: "unknown" });
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [teamKeyMode, setTeamKeyMode] = useState<TeamKeyMode>("unknown");
  const bridgeApiUrl = useRef<string | null>(null);
  const [working, setWorking] = useState(false);
  const bootstrapStarted = useRef(false);

  // GitHub-first: the API says whether sign-in exists and whether it manages the team
  // key, so the gate never shows a dead button or asks for a secret the API already
  // holds. An unreachable API keeps every path visible rather than hiding the fallback.
  useEffect(() => {
    let cancelled = false;
    new DashboardApi(apiUrl, "").githubAvailability()
      .then((availability) => {
        if (cancelled) return;
        setGithub(availability.configured
          ? { state: "configured" }
          : { state: "unconfigured", setupUrl: availability.setupUrl });
        setTeamKeyMode(availability.teamKey === "managed" ? "managed" : "client");
        setInstallUrl(availability.installUrl ?? null);
        bridgeApiUrl.current = availability.apiUrl ?? null;
      })
      .catch(() => { if (!cancelled) { setGithub({ state: "unknown" }); setTeamKeyMode("unknown"); } });
    return () => { cancelled = true; };
  }, [apiUrl]);

  /**
   * Opens the cockpit. With a managed key the API is asked for it right here, so the
   * user never types it; with a client-held key the pasted value is used. Either way the
   * key goes straight to the isolated worker and never touches browser storage.
   */
  const unlockWith = useCallback(async (bearer: string, mode: TeamKeyMode) => {
    setWorking(true); setError(null);
    const worker = new CryptoWorkerClient();
    try {
      const api = new DashboardApi(apiUrl, bearer);
      if (bridgeApiUrl.current) api.bridgeApiUrl = bridgeApiUrl.current;
      const user = await api.me();
      if (mode === "managed") {
        const issued = await api.teamKey();
        await worker.initialize(issued.key, issued.keyId, issued.keyEpoch);
      } else {
        await worker.initialize(teamKey, keyId, keyEpoch);
      }
      setTeamKey(""); setToken("");
      onConnected(api, worker, user);
    } catch (caught) {
      worker.destroy();
      setError(caught instanceof Error ? caught.message : "Could not unlock baton");
    } finally {
      setWorking(false);
    }
  }, [apiUrl, keyEpoch, keyId, onConnected, teamKey]);

  useEffect(() => {
    // GitHub's callback lands on /auth/complete, or straight back on /pair when a terminal sent
    // the visitor there. /pair is also a cold entry point, so a missing grant is not an error.
    const path = window.location.pathname;
    if ((path !== "/auth/complete" && path !== PAIR_PATH && !path.startsWith("/handover/")) || bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    setWorking(true);
    const rawToken = createBrowserToken();
    void sha256Hex(rawToken).then(async (tokenDigest) => {
      const gate = new DashboardApi(apiUrl, "");
      await gate.bootstrapToken({
        id: crypto.randomUUID(),
        idempotencyKey: crypto.randomUUID(),
        tokenDigest,
        encryptedLabel: null,
        labelRevision: 0,
        expiresAt: null,
      });
      setToken(rawToken);
      setSignedInViaGitHub(true);
      setBootstrapNotice("GitHub sign-in complete. Copy this new token for your local bridge before unlocking; it exists only in this page's memory.");
      window.history.replaceState(null, "", path === PAIR_PATH ? PAIR_PATH : "/");
      // A managed key means there is nothing left to type: open the cockpit now.
      const availability = await gate.githubAvailability().catch(() => null);
      if (availability?.teamKey === "managed") await unlockWith(rawToken, "managed");
    }).catch((caught: unknown) => {
      bootstrapStarted.current = false;
      // /pair is a cold entry point: no grant there just means nobody has signed in yet, and the
      // gate below is the answer. On /auth/complete a missing grant is a failed sign-in, and shows.
      if ((path === PAIR_PATH || path.startsWith("/handover/")) && caught instanceof ApiError && caught.code === "login_grant_required") return;
      setError(caught instanceof Error ? caught.message : "Could not finish GitHub sign-in");
    }).finally(() => setWorking(false));
    // The unlock callback is intentionally read once at bootstrap time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  async function beginGitHub() {
    setWorking(true); setError(null);
    try {
      const { authorizeUrl } = await new DashboardApi(apiUrl, "")
        .githubStart({ inviteToken: pendingInvite, next: pairing ? PAIR_PATH : handoverId ? `/handover/${handoverId}` : null });
      window.location.assign(authorizeUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start GitHub sign-in");
      setWorking(false);
    }
  }

  async function unlock(event: React.FormEvent) {
    event.preventDefault();
    await unlockWith(token, teamKeyMode);
  }

  function generateTeamKey() {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    setTeamKey(Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(""));
    setGeneratedKey(true);
  }

  const managed = teamKeyMode === "managed";
  const githubFirst = github.state === "configured" && !signedInViaGitHub;
  const showTokenField = signedInViaGitHub || github.state !== "configured";

  return <main className="keygate-shell">
    <section className="keygate-card">
      <div className="wordmark">baton<span>:</span></div>
      <p className="eyebrow">Pick up where your team left off</p>
      <h1>Sign in.<br />Start collaborating.</h1>
      {managed
        ? <p className="intro">Connect your GitHub account, create a project, and hand over work to your collaborators.</p>
        : <p className="intro">Sign in with GitHub, then use your team's key to open your projects and handovers.</p>}
      {pairing && <aside className="bootstrap-notice pair-pending" data-testid="pair-pending">
        <strong>A terminal on this machine wants to connect to your Baton account.</strong> Sign in, then approve it. The code it printed stays in this page's memory only.
      </aside>}
      {pendingInvite && <aside className="bootstrap-notice invite-pending" data-testid="invite-pending">
        <strong>You were invited to a project.</strong> Sign in to join it; the invitation stays in this page's memory only.
      </aside>}
      {!managed && <aside className="origin-warning">
        <strong>Active-origin limitation</strong>
        A compromised dashboard could steal a key you paste afterward. Verify this origin and TLS before unlocking.
      </aside>}
      <form onSubmit={unlock} autoComplete="off">
        {!signedInViaGitHub && github.state !== "unconfigured" && <button type="button" className={githubFirst ? "primary" : "secondary"} disabled={working} onClick={() => void beginGitHub()}>Sign in with GitHub</button>}
        {githubFirst && installUrl && <p className="key-helper">First project? <a href={installUrl} target="_blank" rel="noreferrer">Connect your GitHub repositories</a>, then sign in.</p>}
        {github.state === "unconfigured" && <aside className="setup-notice">
          <strong>GitHub sign-in is not configured on this API yet.</strong>
          {github.setupUrl
            ? <span>Create the GitHub App from <a href={github.setupUrl} target="_blank" rel="noreferrer">the setup page</a>, add its credentials to <code>.env</code>, and restart the API. Until then, unlock with the development token.</span>
            : <span>Ask the operator to configure the GitHub App. Until then, unlock with a development token.</span>}
        </aside>}
        {signedInViaGitHub && bootstrapNotice && <aside className="bootstrap-notice">{bootstrapNotice}<button type="button" onClick={() => void navigator.clipboard.writeText(token)}>Copy token</button></aside>}
        {showTokenField && <label>Bearer token<input type={signedInViaGitHub ? "text" : "password"} readOnly={signedInViaGitHub} value={token} onChange={(event) => setToken(event.target.value)} required /></label>}
        {!githubFirst && !managed && <>
          <label>Team key<input type="password" value={teamKey} onChange={(event) => setTeamKey(event.target.value)} autoFocus={signedInViaGitHub} required /></label>
          <p className="key-helper">
            <button type="button" className="link" onClick={generateTeamKey}>Generate a new team key</button>
            <span>First owner only; share it with members outside baton.</span>
          </p>
          {generatedKey && <aside className="bootstrap-notice">A new team key is in the field above. Store it in your password manager now; baton never keeps it.<button type="button" onClick={() => void navigator.clipboard.writeText(teamKey)}>Copy key</button></aside>}
        </>}
        <details className="advanced">
          <summary>Advanced</summary>
          <label>API URL<input value={apiUrl} onChange={(event) => setApiUrl(event.target.value)} required /></label>
          {!managed && <div className="field-row">
            <label>Key ID<input value={keyId} onChange={(event) => setKeyId(event.target.value)} required /></label>
            <label>Epoch<input type="number" min="1" value={keyEpoch} onChange={(event) => setKeyEpoch(Number(event.target.value))} required /></label>
          </div>}
        </details>
        {error && <p className="error">{error}</p>}
        {!githubFirst && <button className="primary" disabled={working}>{working ? "Unlocking…" : "Unlock cockpit"}</button>}
      </form>
    </section>
  </main>;
}

type Dialog =
  | { kind: "createAgent"; seed: AgentCreateSeed | null }
  | { kind: "connect"; agent: ConnectAgent }
  | { kind: "repository" }
  | { kind: "invite" }
  | { kind: "createProject" }
  | { kind: "composer"; wireId: string; source: AgentRef; target: AgentRef }
  | { kind: "telegram" }
  | { kind: "handover" }
  | { kind: "messages" }
  | { kind: "readHandover"; messageId: string }
  | null;

const NARROW_QUERY = "(max-width: 900px)";

function Cockpit({ connection, openChecklist, onLock }: { connection: Connection; openChecklist: boolean; onLock(): void }) {
  const { api, worker, user } = connection;
  const [sessions, setSessions] = useState<Session[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [projects, setProjects] = useState<DecryptedProjectSummary[]>([]);
  const [projectInvitations, setProjectInvitations] = useState<DecryptedProjectInvitation[]>([]);
  const [repositories, setRepositories] = useState<RepositoryView[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(connection.joinedProjectId);
  const [loadedProjectGraph, setProjectGraph] = useState<DecryptedProjectGraph | null>(null);
  // Graphs and composers must always belong to the selected project, including while
  // a previous request or dashboard-session initialization is still resolving.
  const selectedProjectRef = useRef(selectedProjectId);
  selectedProjectRef.current = selectedProjectId;
  const projectGraph = loadedProjectGraph?.project.id === selectedProjectId ? loadedProjectGraph : null;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<Dialog>(connection.handoverId ? { kind: "readHandover", messageId: connection.handoverId } : null);
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [selectedNode, setSelectedNode] = useState<BatonNode | null>(null);
  const [listView, setListView] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [realtime, setRealtime] = useState<"connecting" | "connected" | "reconnecting" | "unavailable">("connecting");
  const [invitePreview, setInvitePreview] = useState<InvitePreviewView | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteWorking, setInviteWorking] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<string | null>(connection.pendingInvite);
  const [joinedProject, setJoinedProject] = useState<{ projectName: string } | null>(null);
  const [telegram, setTelegram] = useState<TelegramStatus | null>(null);
  const [checklistOpen, setChecklistOpen] = useState(openChecklist);
  const latestRefresh = useRef(0);
  const latestProjectLoad = useRef(0);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const demoStep = useRef(0);

  const dashboardSession = useDashboardSession(api);
  const projectMessages = useProjectMessages(api, worker, selectedProjectId);

  // A narrow window falls back to the accessible list; widening again does not force the canvas.
  useEffect(() => {
    const media = window.matchMedia(NARROW_QUERY);
    const onChange = (event: MediaQueryListEvent) => { if (event.matches) setListView(true); };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const loadProject = useCallback(async (projectId: string) => {
    if (selectedProjectRef.current !== projectId) return;
    const request = ++latestProjectLoad.current;
    try {
      const encryptedGraph = await api.project(projectId);
      const decryptedGraph = await worker.call<DecryptedProjectGraph>("decryptProjectGraph", {
        graph: encryptedGraph satisfies ProjectGraph,
      });
      if (request !== latestProjectLoad.current || selectedProjectRef.current !== projectId) return;
      setProjectGraph(decryptedGraph);
    } catch (caught) {
      if (request !== latestProjectLoad.current || selectedProjectRef.current !== projectId) return;
      setProjectGraph(null);
      setError(caught instanceof Error ? caught.message : "Project refresh failed");
    }
  }, [api, worker]);

  const refresh = useCallback(async () => {
    const request = ++latestRefresh.current;
    try {
      const [nextSessions, nextAgents, encryptedProjects, encryptedInvitations, nextRepositories] = await Promise.all([
        api.sessions("team"), api.agents("team"), api.projects(), api.projectInvitations(), api.repositories(),
      ]);
      const [decryptedProjects, decryptedInvitations] = await Promise.all([
        worker.call<DecryptedProjectSummary[]>("decryptProjects", {
          projects: encryptedProjects satisfies ProjectSummary[],
        }),
        worker.call<DecryptedProjectInvitation[]>("decryptProjectInvitations", {
          invitations: encryptedInvitations satisfies ProjectInvitation[],
        }),
      ]);
      if (request !== latestRefresh.current) return;
      setSessions(nextSessions); setAgents(nextAgents);
      setProjects(decryptedProjects); setProjectInvitations(decryptedInvitations); setRepositories(nextRepositories);
      setSelectedProjectId((current) => current && decryptedProjects.some((project) => project.id === current)
        ? current
        : decryptedProjects[0]?.id ?? null);
      setError(null);
    } catch (caught) {
      if (request === latestRefresh.current) setError(caught instanceof Error ? caught.message : "Refresh failed");
    } finally {
      if (request === latestRefresh.current) setLoading(false);
    }
  }, [api, worker]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => { refreshTimer.current = null; void refresh(); }, 1_500);
  }, [refresh]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (selectedProjectId) void loadProject(selectedProjectId);
    else setProjectGraph(null);
  }, [loadProject, selectedProjectId, projects]);
  useEffect(() => {
    setChatTarget(null); setEvents([]); setSelectedNode(null);
    setDialog((current) => current?.kind === "readHandover" ? current : null);
  }, [selectedProjectId]);

  // Ensuring the dashboard session also backfills this member's general agent into every
  // project they belong to, so the "you" block and chat are ready before the first message.
  const generalEnsured = useRef(false);
  useEffect(() => {
    if (!selectedProjectId || generalEnsured.current) return;
    generalEnsured.current = true;
    // A failure is not retried here: opening a chat ensures the session again and reports the error.
    void dashboardSession.ensure()
      .then(() => loadProject(selectedProjectId))
      .catch(() => undefined);
  }, [selectedProjectId, dashboardSession, loadProject]);

  // Presence comes from heartbeats, which emit no events: poll the session list gently.
  useEffect(() => {
    if (!selectedProjectId) return;
    const timer = setInterval(() => { void api.sessions("team").then(setSessions).catch(() => undefined); }, 30_000);
    return () => clearInterval(timer);
  }, [api, selectedProjectId]);

  useEffect(() => {
    if (events.length === 0) return;
    const timer = setInterval(() => {
      setEvents((current) => {
        const next = pruneActivity(current, Date.now());
        return next.length === current.length ? current : next;
      });
    }, 500);
    return () => clearInterval(timer);
  }, [events.length]);

  const pushEvent = useCallback((event: ActivityEvent) => {
    setEvents((current) => reduceActivity(current, event, Date.now()));
  }, []);

  const activityContext = useMemo(() => {
    if (!projectGraph) return null;
    const nodeIds = new Set(projectGraph.agentAssignments.map((assignment) => agentNodeId(assignment.agent.id)));
    const wireEdges = new Map<string, string>();
    for (const wire of projectGraph.canvas?.wires ?? []) wireEdges.set(`${wire.sourceAgentId}|${wire.targetAgentId}`, `wire:${wire.id}`);
    return { projectId: projectGraph.project.id, nodeIds, wireEdges };
  }, [projectGraph]);
  const activityContextRef = useRef(activityContext);
  activityContextRef.current = activityContext;

  const onRealtimeEvent = useCallback((event: OutboxEvent) => {
    if (event.topic.startsWith("message.")) {
      const handled = projectMessages.onEvent(event);
      const context = activityContextRef.current;
      if (handled && context) {
        const payload = event.payload as Partial<RealtimeMessageEvent>;
        if (payload.senderAgentId && payload.recipientAgentIds && payload.type) {
          const packets = activityFromRealtime({
            id: String(event.id),
            topic: event.topic,
            messageId: payload.messageId ?? event.aggregateId,
            type: payload.type,
            projectId: payload.projectId ?? null,
            senderAgentId: payload.senderAgentId,
            recipientAgentIds: payload.recipientAgentIds,
            ...(payload.agentId ? { agentId: payload.agentId } : {}),
            ...(payload.state ? { state: payload.state } : {}),
            at: Date.parse(event.createdAt) || Date.now(),
          }, context);
          for (const packet of packets) pushEvent(packet);
        }
      }
      return;
    }
    if (event.topic.startsWith("project.")) {
      const payload = event.payload as { projectId?: string; updatedByUserId?: string };
      const projectId = payload.projectId ?? event.aggregateId;
      const selfLayout = event.topic === "project.canvas_updated" && payload.updatedByUserId === user.id;
      if (projectId === selectedProjectId && !selfLayout) void loadProject(projectId);
      if (!selfLayout) scheduleRefresh();
      return;
    }
    scheduleRefresh();
  }, [projectMessages, pushEvent, scheduleRefresh, selectedProjectId, loadProject, user.id]);

  useRealtime(api, onRealtimeEvent, (status) => setRealtime(status));

  // Invite link opened while already signed in (or via the development token): preview, then accept.
  useEffect(() => {
    if (!pendingInvite) return;
    let cancelled = false;
    setInviteError(null);
    api.previewInviteLink(pendingInvite)
      .then((preview) => worker.call<InvitePreviewView & { membership: { state: string } | null }>("decryptInviteLinkPreview", { preview }))
      .then((decrypted) => {
        if (cancelled) return;
        setInvitePreview({
          projectId: decrypted.projectId,
          name: decrypted.name,
          invitedByHandle: decrypted.invitedByHandle,
          role: decrypted.role,
          activeMemberCount: decrypted.activeMemberCount,
          alreadyMember: decrypted.membership?.state === "active",
        });
      })
      .catch((caught: unknown) => { if (!cancelled) setInviteError(caught instanceof Error ? caught.message : "This invite link is not valid"); });
    return () => { cancelled = true; };
  }, [api, worker, pendingInvite]);

  async function acceptInvite() {
    if (!pendingInvite || !invitePreview) return;
    setInviteWorking(true); setInviteError(null);
    try {
      const graph = await api.acceptInviteLink(pendingInvite);
      setPendingInvite(null); setInvitePreview(null);
      // The dialog stays up on the Telegram hand-off card rather than closing on the join.
      setJoinedProject({ projectName: invitePreview.name });
      await refresh();
      setSelectedProjectId(graph.project.id);
      setNotice(`You joined ${invitePreview.name}.`);
    } catch (caught) {
      setInviteError(caught instanceof Error ? caught.message : "Could not accept the invitation");
    } finally {
      setInviteWorking(false);
    }
  }

  const savePositions = useCallback((projectId: string, positions: PositionChange[]) =>
    api.saveCanvasPositions(projectId, positions), [api]);
  const canvasPositions = useCanvasPositions(selectedProjectId, projectGraph?.canvas?.positions, savePositions);

  const ownAgents = useMemo(
    () => agents.filter((agent) => agent.ownerUserId === user.id && !agent.retiredAt),
    [agents, user.id],
  );
  const viewer: FlowViewer = useMemo(() => ({
    userId: user.id,
    generalAgentId: user.generalAgentId ?? ownAgents.find((agent) => agent.role === null)?.id ?? null,
    isAdmin: projectGraph ? ["owner", "admin"].includes(projectGraph.project.role) : false,
    isOwner: projectGraph?.project.role === "owner",
  }), [user.id, user.generalAgentId, ownAgents, projectGraph]);

  // One read of the project's people for the whole cockpit: the rail and the invite dialog share it.
  const people = useProjectPeople({
    api,
    graph: projectGraph,
    agents,
    canInvite: viewer.isAdmin,
    origin: window.location.origin,
  });

  // The rail's Telegram row is read once and again whenever the Telegram dialog closes, so a chat
  // linked inside the dialog shows up without polling the relay from the rail.
  const telegramOpen = dialog?.kind === "telegram";
  useEffect(() => {
    if (telegramOpen) return;
    let cancelled = false;
    api.telegramStatus()
      .then((status) => { if (!cancelled) setTelegram(status); })
      .catch(() => { if (!cancelled) setTelegram(null); });
    return () => { cancelled = true; };
  }, [api, telegramOpen]);

  const linkedRepositoryIds = useMemo(() => new Set(projectGraph?.repositories.map((repository) => repository.id) ?? []), [projectGraph]);
  const linkableRepositories = useMemo(
    () => repositories.filter((repository) => !linkedRepositoryIds.has(repository.id)),
    [repositories, linkedRepositoryIds],
  );

  const runAction = useCallback(async (action: () => Promise<void>, success?: string) => {
    try {
      await action();
      if (success) setNotice(success);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Action failed");
    }
  }, []);

  const agentRef = useCallback((agentId: string): ConnectAgent | null => {
    const agent = agents.find((candidate) => candidate.id === agentId)
      ?? projectGraph?.agentAssignments.find((assignment) => assignment.agent.id === agentId)?.agent
      ?? null;
    return agent ? { id: agent.id, handle: agent.handle, role: agent.role, color: agent.color, listeningNote: agent.listeningNote ?? null, listeningNoteRevision: agent.listeningNoteRevision ?? 0 } : null;
  }, [agents, projectGraph]);

  const projectRepositories = useMemo(() => projectGraph?.repositories ?? [], [projectGraph]);
  const projectRef = useMemo(() => projectGraph ? { id: projectGraph.project.id, name: projectGraph.project.name } : null, [projectGraph]);

  const handleFor = useCallback((agentId: string): string => {
    const agent = agents.find((candidate) => candidate.id === agentId)
      ?? projectGraph?.agentAssignments.find((assignment) => assignment.agent.id === agentId)?.agent;
    if (!agent) return agentId.slice(0, 8);
    const owner = ("ownerHandle" in agent ? agent.ownerHandle : undefined)
      ?? projectGraph?.members.find((member) => member.userId === agent.ownerUserId)?.handle;
    return agentShortName(agent, owner);
  }, [agents, projectGraph]);

  const flowActions: FlowActions = useMemo(() => ({
    chat: (agent: AgentRef) => setChatTarget({ kind: "agent", agentId: agent.agentId }),
    openWire: (wire: WireRef) => setChatTarget({ kind: "wire", ...wire }),
    connect: (agent: AgentRef) => {
      const resolved = agentRef(agent.agentId);
      if (resolved) setDialog({ kind: "connect", agent: resolved });
    },
    assign: async (agentId, repositoryId) => {
      if (!projectGraph) return;
      await runAction(async () => {
        await api.assignProjectAgent(projectGraph.project.id, agentId, repositoryId);
        await loadProject(projectGraph.project.id);
      }, "Agent assigned.");
    },
    removeAssignment: async (assignmentId) => {
      if (!projectGraph) return;
      await runAction(async () => {
        await api.removeProjectAgent(projectGraph.project.id, assignmentId);
        await loadProject(projectGraph.project.id);
      }, "Assignment removed.");
    },
    createWire: async (wire) => {
      if (!projectGraph) return;
      const projectId = projectGraph.project.id;
      try {
        await api.createWire(projectId, wire);
        setError(null);
        await loadProject(projectId);
        // The wire exists; now decide what travels along it first.
        setDialog({
          kind: "composer",
          wireId: wire.id,
          source: { agentId: wire.sourceAgentId, handle: handleFor(wire.sourceAgentId) },
          target: { agentId: wire.targetAgentId, handle: handleFor(wire.targetAgentId) },
        });
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Could not create the wire");
        throw caught;
      }
    },
    removeWire: async (wireId) => {
      if (!projectGraph) return;
      await runAction(async () => {
        await api.removeWire(projectGraph.project.id, wireId);
        setChatTarget((current) => current?.kind === "wire" && current.wireId === wireId ? null : current);
        await loadProject(projectGraph.project.id);
      }, "Wire removed.");
    },
    createAgent: (seed) => setDialog({ kind: "createAgent", seed: seed ?? null }),
    addRepository: () => setDialog({ kind: "repository" }),
    unlinkRepository: (repositoryId, label) => {
      if (!projectGraph) return;
      if (!window.confirm(`Unlink ${label} from this project? Agents assigned only to it lose that assignment.`)) return;
      void runAction(async () => {
        await api.removeProjectRepository(projectGraph.project.id, repositoryId);
        await refresh();
        await loadProject(projectGraph.project.id);
      }, `${label} unlinked.`);
    },
    invite: () => setDialog({ kind: "invite" }),
    permissions: { isAdmin: viewer.isAdmin, isOwner: viewer.isOwner },
    repositories: (projectGraph?.repositories ?? []).map((repository) => ({ id: repository.id, label: `${repository.ownerHandle}/${repository.name}` })),
    ownAgents: ownAgents.map((agent) => ({ agentId: agent.id, handle: agent.handle })),
  }), [agentRef, handleFor, projectGraph, runAction, api, loadProject, refresh, viewer.isAdmin, viewer.isOwner, ownAgents]);

  const presence = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, Presence>();
    for (const assignment of projectGraph?.agentAssignments ?? []) {
      if (!map.has(assignment.agent.id)) map.set(assignment.agent.id, presenceFor(assignment.agent.id, sessions, now));
    }
    return map;
  }, [projectGraph, sessions]);

  // Setup is derived from what the cockpit already holds: no stored flags, no wizard state.
  const setupSteps = useMemo(() => deriveSetupSteps({
    projectCount: projects.length,
    projectLoaded: projectGraph !== null,
    repositoryCount: projectGraph?.repositories.length ?? 0,
    otherMemberCount: (projectGraph?.members ?? [])
      .filter((member) => member.userId !== user.id && member.state !== "revoked").length,
    telegram: telegram ? { available: telegram.available, linked: telegram.linked } : null,
    // A bound non-dashboard client is exactly what `baton open` produces.
    ownAgents: ownAgents.map((agent) => ({
      name: agentShortName(agent, user.handle),
      opened: presenceFor(agent.id, sessions, Date.now()).online,
    })),
    linkableRepositoryCount: linkableRepositories.length,
    canAdmin: viewer.isAdmin,
  }), [projects.length, projectGraph, telegram, ownAgents, sessions, user.id, user.handle, linkableRepositories.length, viewer.isAdmin]);

  const onSetupAction = useCallback((kind: SetupActionKind) => {
    if (kind === "createProject") setDialog({ kind: "createProject" });
    else if (kind === "repository") setDialog({ kind: "repository" });
    else if (kind === "invite") setDialog({ kind: "invite" });
    else setDialog({ kind: "telegram" });
  }, []);

  const sendChat = useCallback(async (draft: SendDraft) => {
    const projectId = projectGraph?.project.id;
    if (!projectId || selectedProjectRef.current !== projectId || selectedProjectId !== projectId) throw new Error("The selected project changed. Open the composer again.");
    const session = await dashboardSession.ensure();
    if (selectedProjectRef.current !== projectId) throw new Error("The selected project changed before sending. Open the composer again.");
    await projectMessages.send({ sessionId: session.sessionId, senderAgentId: session.agentId }, draft);
  }, [dashboardSession, projectMessages, projectGraph, selectedProjectId]);

  const composing = dialog?.kind === "composer";
  useEffect(() => {
    if (!chatTarget && !composing) return;
    void dashboardSession.ensure().catch((caught: unknown) => {
      setError(caught instanceof ApiError && caught.code === "general_agent_required"
        ? "Your account has no general agent yet; sign in with GitHub once to create it."
        : caught instanceof Error ? caught.message : "Could not prepare the dashboard session");
    });
  }, [chatTarget, composing, dashboardSession]);

  const onAgentCreated = useCallback(async (agent: Agent, _assignment: ProjectAgentAssignment) => {
    const seed = dialog?.kind === "createAgent" ? dialog.seed : null;
    if (seed?.position && selectedProjectId) {
      canvasPositions.applyChanges([{ nodeId: agentNodeId(agent.id), x: seed.position.x, y: seed.position.y }]);
    }
    setDialog({ kind: "connect", agent: { id: agent.id, handle: agent.handle, role: agent.role, color: agent.color, listeningNote: agent.listeningNote ?? null, listeningNoteRevision: agent.listeningNoteRevision ?? 0 } });
    await refresh();
    if (selectedProjectId) await loadProject(selectedProjectId);
    setNotice(`${agent.handle} is on the canvas. Connect a client on your machine, or open a chat.`);
  }, [dialog, selectedProjectId, canvasPositions, refresh, loadProject]);

  const acceptLegacyInvitation = useCallback((projectId: string) => {
    void runAction(async () => {
      await api.acceptProjectInvitation(projectId);
      await refresh();
      setSelectedProjectId(projectId);
    }, "Invitation accepted.");
  }, [api, refresh, runAction]);

  const revokeMember = useCallback((member: ProjectMemberView) => {
    if (!projectGraph) return;
    if (!window.confirm(`Remove ${member.handle} from ${projectGraph.project.name}? Their agents leave the canvas.`)) return;
    void runAction(async () => {
      await api.revokeProjectMember(projectGraph.project.id, member.userId);
      await loadProject(projectGraph.project.id);
    }, `${member.handle} removed.`);
  }, [api, loadProject, projectGraph, runAction]);

  /** Development only: walks one edge through every routing state without touching the network. */
  const playDemoPacket = useMemo(() => {
    if (!import.meta.env.DEV || !projectGraph) return undefined;
    const edges = buildProjectFlow(projectGraph, buildOptionsFor(viewer)).edges;
    const edge = edges.find((candidate) => candidate.data?.relation === "wire") ?? edges[0];
    if (!edge) return undefined;
    return () => pushEvent(createDemoActivityEvent({ projectId: projectGraph.project.id, edgeId: edge.id, step: demoStep.current++, now: Date.now() }));
  }, [projectGraph, viewer, pushEvent]);

  const latestEvent = events.length > 0 ? events[events.length - 1] ?? null : null;

  return <div className={`cockpit stage-layout${chatTarget ? " has-chat" : ""}`}>
    <FlowActionsProvider actions={flowActions}>
      <PresenceProvider presence={presence}>
        <FlowSidebar
          userHandle={user.handle}
          projects={projects}
          invitations={projectInvitations}
          selectedProjectId={selectedProjectId}
          selectProject={setSelectedProjectId}
          graph={projectGraph}
          viewer={viewer}
          loading={loading}
          realtime={realtime}
          listView={listView}
          setListView={setListView}
          selectedNode={selectedNode}
          linkableRepositoryCount={linkableRepositories.length}
          onNewProject={() => setDialog({ kind: "createProject" })}
          onTelegram={() => setDialog({ kind: "telegram" })}
          onSetup={() => setChecklistOpen((open) => !open)}
          onHandover={() => setDialog({ kind: "handover" })}
          onMessages={() => setDialog({ kind: "messages" })}
          setupRemaining={setupProgress(setupSteps).remaining}
          people={people}
          telegram={telegram}
          onAcceptInvitation={acceptLegacyInvitation}
          onTidy={(positions) => canvasPositions.applyChanges(positions)}
          onDemoPacket={playDemoPacket}
          onRevokeMember={revokeMember}
          onChangeRole={(member, role) => {
            if (!projectGraph) return;
            void runAction(async () => {
              await api.updateProjectMemberRole(projectGraph.project.id, member.userId, role);
              await loadProject(projectGraph.project.id);
            }, `@${member.handle} is now a project ${role}.`);
          }}
          onLock={onLock}
        />
        <main className="workspace stage">
          {(error || notice) && <div className="stage-banners">
            {error && <p className="banner error">{error}<button type="button" className="link" onClick={() => setError(null)}>dismiss</button></p>}
            {notice && <p className="banner">{notice}<button type="button" className="link" onClick={() => setNotice(null)}>dismiss</button></p>}
          </div>}
          <FlowHome
            hasProjects={projects.length > 0}
            invitations={projectInvitations}
            graph={projectGraph}
            selectedProjectId={selectedProjectId}
            viewer={viewer}
            loading={loading}
            listView={listView}
            positions={canvasPositions.positions}
            onPositionsChange={canvasPositions.applyChanges}
            onTidy={(positions) => canvasPositions.applyChanges(positions)}
            events={events}
            onSelect={setSelectedNode}
            onCreateProject={() => setDialog({ kind: "createProject" })}
            onAcceptInvitation={acceptLegacyInvitation}
          />
          {checklistOpen && <SetupChecklist
            steps={setupSteps}
            onAction={onSetupAction}
            onDismiss={() => setChecklistOpen(false)}
          />}
          <p className="stage-status" role="status" aria-live="polite">
            {latestEvent
              ? `Activity packet ${describeActivity(latestEvent.state).label} on the project graph.`
              : projectGraph ? `${projectGraph.project.name}: ${projectGraph.canvas?.wires.length ?? 0} conversation wires.` : ""}
          </p>
        </main>
      </PresenceProvider>
    </FlowActionsProvider>

    {chatTarget && projectGraph && <AgentChatDrawer
      target={chatTarget}
      graph={projectGraph}
      viewer={viewer}
      agents={agents}
      sessions={sessions}
      messages={projectMessages.messages}
      sessionReady={dashboardSession.session !== null}
      error={projectMessages.error}
      onSend={sendChat}
      onClose={() => setChatTarget(null)}
      onConnect={(agent) => { const resolved = agentRef(agent.agentId); if (resolved) setDialog({ kind: "connect", agent: resolved }); }}
    />}

    {dialog?.kind === "handover" && projectGraph && <HandoverDialog key={projectGraph.project.id} graph={projectGraph} userId={user.id} onSend={sendChat} onClose={() => setDialog(null)} />}
    {dialog?.kind === "messages" && projectGraph && <ProjectMessagesDialog
      graph={projectGraph} userId={user.id} messages={projectMessages.messages} error={projectMessages.error}
      onChat={(agentId) => { setDialog(null); setChatTarget({ kind: "agent", agentId }); }}
      onHandover={(messageId) => setDialog({ kind: "readHandover", messageId })} onClose={() => setDialog(null)}
    />}
    {dialog?.kind === "readHandover" && <HandoverReader messageId={dialog.messageId} api={api} worker={worker} ownAgents={ownAgents} onProject={setSelectedProjectId} onClose={() => setDialog(null)} />}
    <WireComposer
      open={dialog?.kind === "composer"}
      source={dialog?.kind === "composer" ? dialog.source : null}
      target={dialog?.kind === "composer" ? dialog.target : null}
      viewerGeneralAgentId={viewer.generalAgentId}
      sessionReady={dashboardSession.session !== null}
      project={projectRef}
      repositories={projectRepositories}
      onSend={async (draft) => {
        if (dialog?.kind !== "composer") return;
        const wire = dialog;
        await sendChat(draft);
        setDialog(null);
        setChatTarget({ kind: "wire", wireId: wire.wireId, sourceAgentId: wire.source.agentId, targetAgentId: wire.target.agentId });
      }}
      onSkip={() => {
        if (dialog?.kind !== "composer") return;
        const wire = dialog;
        setDialog(null);
        setChatTarget({ kind: "wire", wireId: wire.wireId, sourceAgentId: wire.source.agentId, targetAgentId: wire.target.agentId });
      }}
      onClose={() => setDialog(null)}
    />

    {projectGraph && <AgentCreateDialog
      open={dialog?.kind === "createAgent"}
      onClose={() => setDialog(null)}
      api={api}
      user={user}
      ownAgents={ownAgents}
      graph={projectGraph}
      seed={dialog?.kind === "createAgent" ? dialog.seed : null}
      onCreated={onAgentCreated}
    />}
    <AgentConnectSheet
      open={dialog?.kind === "connect"}
      onClose={() => setDialog(null)}
      agent={dialog?.kind === "connect" ? dialog.agent : null}
      apiUrl={api.bridgeApiUrl}
      ownerHandle={user.handle}
      projectName={projectGraph?.project.name ?? null}
      api={api}
      worker={worker}
      canChat={dialog?.kind === "connect" ? dialog.agent.id !== viewer.generalAgentId : false}
      onOpenChat={(agent) => { setDialog(null); setChatTarget({ kind: "agent", agentId: agent.id }); }}
      onListeningNoteSaved={() => { void refresh(); }}
    />
    <TelegramDialog open={dialog?.kind === "telegram"} onClose={() => setDialog(null)} api={api} />
    <RepositoryPicker
      api={api}
      onRefresh={refresh}
      open={dialog?.kind === "repository"}
      onClose={() => setDialog(null)}
      repositories={linkableRepositories}
      working={false}
      onAdd={(repositoryId) => {
        if (!projectGraph) return;
        void runAction(async () => {
          await api.addProjectRepository(projectGraph.project.id, repositoryId);
          setDialog(null);
          await refresh();
          await loadProject(projectGraph.project.id);
        }, "Repository linked to the project.");
      }}
    />
    <ProjectCreateDialog
      onRefreshRepositories={refresh}
      open={dialog?.kind === "createProject"}
      onClose={() => setDialog(null)}
      api={api}
      worker={worker}
      repositories={repositories}
      onCreated={async (projectId) => {
        setDialog(null);
        await refresh();
        setSelectedProjectId(projectId);
        setNotice("Project created. Your general agent is already on its canvas.");
      }}
    />
    {projectGraph && <InviteDialog
      open={dialog?.kind === "invite"}
      onClose={() => setDialog(null)}
      api={api}
      projectId={projectGraph.project.id}
      projectName={projectGraph.project.name}
      isOwner={viewer.isOwner}
      people={people}
    />}
    <InviteAcceptDialog
      open={pendingInvite !== null || joinedProject !== null}
      preview={invitePreview}
      error={inviteError}
      working={inviteWorking}
      joined={joinedProject}
      onAccept={() => void acceptInvite()}
      onDecline={() => { setPendingInvite(null); setInvitePreview(null); setInviteError(null); setJoinedProject(null); }}
      onConnectTelegram={() => { setJoinedProject(null); setDialog({ kind: "telegram" }); }}
    />
  </div>;
}

function createBrowserToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const base64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
  return `baton_${base64}`;
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
